import { emptyReview, LEGACY_REVIEW_STORAGE_KEY, NOTE_FIELDS, PROPOSED_CHANGE_TYPES, REVIEW_DECISIONS, REVIEW_RECOVERY_KEY, REVIEW_STORAGE_KEY, REVIEW_STORAGE_VERSION } from './constants.js'

const decisionValues = new Set(REVIEW_DECISIONS.map(([value]) => value))

export function normaliseReview(value = {}) {
  const review = emptyReview()
  review.decision = decisionValues.has(value.decision) ? value.decision : ''
  for (const field of NOTE_FIELDS) review[field] = typeof value[field] === 'string' ? value[field] : ''
  review.proposed_changes = Array.isArray(value.proposed_changes) ? value.proposed_changes.filter((change) => {
    const specification = PROPOSED_CHANGE_TYPES[change?.change_type]
    return specification && change.target_entity === specification.entity && specification.fields.includes(change.field)
      && ['target_id', 'proposed_value', 'rationale'].every((field) => typeof change[field] === 'string')
  }).map((change) => ({ ...change })) : []
  return review
}

function sessionId(idFactory = () => globalThis.crypto?.randomUUID?.()) {
  return idFactory() || `local-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export function emptyReviewState(artifact, now = new Date(), idFactory) {
  const timestamp = now.toISOString()
  return {
    storage_version: REVIEW_STORAGE_VERSION,
    review_session_id: sessionId(idFactory),
    reviewer_reference: '',
    reviewer_display_name: '',
    review_started_at: timestamp,
    review_updated_at: timestamp,
    coverage_status: 'partial',
    artifact_version: artifact.artifact_version,
    source_pack_version: artifact.source_pack_version,
    contract_version: artifact.contract_version,
    projection_digest: artifact.projection_digest,
    reviews: {},
  }
}

export function isCompatibleReviewState(value, artifact) {
  if (!value || value.storage_version !== REVIEW_STORAGE_VERSION) return false
  if (value.artifact_version !== artifact.artifact_version || value.source_pack_version !== artifact.source_pack_version || value.contract_version !== artifact.contract_version || value.projection_digest !== artifact.projection_digest) return false
  if (!value.review_session_id || !value.review_started_at || !value.review_updated_at || !['partial', 'complete'].includes(value.coverage_status)) return false
  if (!value.reviews || typeof value.reviews !== 'object' || Array.isArray(value.reviews)) return false
  const known = new Set(artifact.records.map((record) => record.character_id))
  return Object.keys(value.reviews).every((characterId) => known.has(characterId))
}

export function loadReviewState(artifact, storage = globalThis.localStorage, now = new Date(), idFactory) {
  if (!storage) return emptyReviewState(artifact, now, idFactory)
  try {
    const raw = storage.getItem(REVIEW_STORAGE_KEY)
    if (!raw) {
      const legacyRaw = storage.getItem(LEGACY_REVIEW_STORAGE_KEY)
      if (!legacyRaw) return emptyReviewState(artifact, now, idFactory)
      const legacy = JSON.parse(legacyRaw)
      const compatible = legacy?.artifact_version === artifact.artifact_version || legacy?.artifact_version === '1.0.0'
      if (!compatible || legacy?.source_pack_version !== artifact.source_pack_version || legacy?.contract_version !== artifact.contract_version || !legacy?.reviews || typeof legacy.reviews !== 'object') {
        return emptyReviewState(artifact, now, idFactory)
      }
      const migrated = { ...emptyReviewState(artifact, now, idFactory), reviews: Object.fromEntries(Object.entries(legacy.reviews).map(([id, review]) => [id, normaliseReview(review)])) }
      storage.setItem(REVIEW_STORAGE_KEY, JSON.stringify(migrated))
      storage.removeItem(LEGACY_REVIEW_STORAGE_KEY)
      return migrated
    }
    const value = JSON.parse(raw)
    if (!isCompatibleReviewState(value, artifact)) {
      storage.setItem(REVIEW_RECOVERY_KEY, raw)
      return { ...emptyReviewState(artifact, now, idFactory), compatibility_notice: `An incompatible local review was preserved under ${REVIEW_RECOVERY_KEY}. A fresh session was started.` }
    }
    return {
      ...value,
      reviews: Object.fromEntries(Object.entries(value.reviews).map(([id, review]) => [id, normaliseReview(review)])),
    }
  } catch {
    storage.removeItem(REVIEW_STORAGE_KEY)
    return emptyReviewState(artifact)
  }
}

export function saveReviewState(state, storage = globalThis.localStorage, now = new Date()) {
  if (!storage) return
  storage.setItem(REVIEW_STORAGE_KEY, JSON.stringify({ ...state, review_updated_at: now.toISOString() }))
}

export function clearReviewState(storage = globalThis.localStorage) {
  storage?.removeItem(REVIEW_STORAGE_KEY)
  storage?.removeItem(LEGACY_REVIEW_STORAGE_KEY)
}
